import { DateFormats, formatDate, formatNameFirstLast, sortAsc } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters, PrimaryData, SecondaryData } from '@strata/react-grid';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router';

import {
    RequestStatusIndicatorMap,
    UNASSIGNED_FILTER,
    UNASSIGNED_LABEL,
} from 'services/requestHelpers';
import { AppRoutes } from 'services/routes';
import { MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import {
    useGetResolvedRequestFilterOptionsQuery,
    useGetResolvedRequestsQuery,
} from 'store/api/agoraMonitorApi';
import { useGetUsersQuery } from 'store/api/userSupportApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const initialSortProperties = [{ id: 'resolvedAtUtc', desc: true }];
// Keep in sync with agoraMonitorApi getResolvedRequests `page_size`.
const RESOLVED_PAGE_SIZE = 50;

const columns = [
    {
        accessorKey: 'requestCategoryName',
        enableSorting: false,
        header: 'Request Category',
        size: 160,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    { accessorKey: 'patientName', enableSorting: false, header: 'Patient', size: 160 },
    { accessorKey: 'drugName', enableSorting: false, header: 'Drug', size: 130 },
    {
        accessorKey: 'pharmacyName',
        enableSorting: false,
        header: 'Pharmacy',
        size: 160,
        cell: ({ getValue, row }) => {
            const name = getValue();
            const npi = row.original.pharmacyNpi;
            return (
                <SecondaryData
                    data={name ?? npi}
                    customTooltipContent={
                        name || npi ? [name, npi && `NPI ${npi}`].filter(Boolean).join(' · ') : null
                    }
                />
            );
        },
    },
    {
        accessorKey: 'state',
        enableSorting: false,
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => {
            const indicator = RequestStatusIndicatorMap[getValue()];
            return (
                <StatusIndicator
                    status={indicator?.status ?? StatusIndicator.Status.DEFAULT}
                    message={indicator?.message ?? getValue()}
                />
            );
        },
    },
    { accessorKey: 'assignedTo', enableSorting: false, header: 'Assignee', size: 160 },
    {
        accessorKey: 'resolvedAtUtc',
        enableSorting: true,
        header: 'Resolved At',
        size: 140,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
];

export const ResolvedRequestsListPage = () => {
    const { showWorkflowManagementLayer = false } = useFlags();
    const navigate = useNavigate();
    const {
        activeFilters,
        filterPaneWidth,
        page,
        sort,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSort,
        updateCurrentPage,
    } = useGridState(Grids.REQUESTS_RESOLVED, initialSortProperties);

    const { data: filterOptions } = useGetResolvedRequestFilterOptionsQuery(undefined, {
        skip: !showWorkflowManagementLayer,
        refetchOnMountOrArgChange: false,
    });
    const { data: supportUsers } = useGetUsersQuery(undefined, {
        skip: !showWorkflowManagementLayer,
    });

    // Chip value is the display label (matches open Requests) so a client-side filter pass
    // still matches rows; map to the API sentinel only when calling the server.
    const assignedToFilter = activeFilters?.assignedTo?.[0];
    const { data, isLoading, isFetching } = useGetResolvedRequestsQuery(
        {
            page,
            sort,
            assignedTo:
                assignedToFilter === UNASSIGNED_LABEL ? UNASSIGNED_FILTER : assignedToFilter,
            requestTypeCode: activeFilters?.requestTypeCode?.[0],
            requestCategoryKey: activeFilters?.requestCategoryKey?.[0],
        },
        { skip: !showWorkflowManagementLayer, refetchOnMountOrArgChange: false },
    );

    const userNameMap = useMemo(() => {
        const map = {};
        supportUsers?.forEach((user) => {
            map[user.upn] = formatNameFirstLast(user.first_name, user.last_name);
        });
        return map;
    }, [supportUsers]);

    const displayRequests = useMemo(
        () =>
            data?.requests?.map((request) => ({
                ...request,
                assignedTo:
                    userNameMap[request.assignedTo] ?? request.assignedTo ?? UNASSIGNED_LABEL,
            })),
        [data?.requests, userNameMap],
    );

    const filterCategories = useMemo(
        () => [
            {
                key: 'requestTypeCode',
                title: 'Request Type',
                customValues: filterOptions?.requestTypes?.map((option) => ({
                    value: option.value,
                    text: option.label,
                })),
            },
            {
                key: 'requestCategoryKey',
                title: 'Request Category',
                customValues: filterOptions?.requestCategories?.map((option) => ({
                    value: option.value,
                    text: option.label,
                })),
            },
            {
                key: 'assignedTo',
                title: 'Assignee',
                // Same value as the cell label so GridWithFilters client filtering cannot
                // drop server-matched unassigned rows (see open Requests list).
                customValues: [
                    { value: UNASSIGNED_LABEL, text: UNASSIGNED_LABEL },
                    ...Object.entries(userNameMap)
                        .map(([upn, text]) => ({ value: upn, text }))
                        .sort((a, b) => sortAsc(a.text, b.text)),
                ],
            },
        ],
        [filterOptions, userNameMap],
    );

    const handleRowClick = ({ referralId }) => {
        if (!referralId) {
            return;
        }
        navigate(
            { pathname: AppRoutes.referralRedirect(referralId), hash: MESSAGES_LOCATION_HASH },
            {
                state: {
                    backToDestination: {
                        text: 'Back to Resolved Requests',
                        route: AppRoutes.requestsResolved,
                    },
                },
            },
        );
    };

    if (!showWorkflowManagementLayer) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Resolved Requests"
                data={displayRequests}
                columns={columns}
                isLoading={isLoading || (isFetching && !data)}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSorting
                manualSorting
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={page}
                countPerPage={RESOLVED_PAGE_SIZE}
                totalCount={data?.total}
                onPageChange={updateCurrentPage}
                onRowClick={handleRowClick}
            />
        </FormPageContainer>
    );
};
